import * as React from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDefinitions } from "../../hooks/useDefinitions";
import { processDefinitions } from "../../services/review.service";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Loader } from "lucide-react";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/taskpane/components/ui/field";

const generateReviewSchema = z
  .object({
    selectedOptions: z.array(z.string()).min(1, "Please select at least one review option"),
    representingInput: z.string().optional(),
    considerationInput: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.selectedOptions.includes("custom-review")) {
        return data.considerationInput && data.considerationInput.trim().length > 0;
      }
      return true;
    },
    {
      message: "Please provide something to consider for custom review",
      path: ["considerationInput"],
    }
  );

export type GenerateReviewFormData = z.infer<typeof generateReviewSchema>;

export interface GenerateReviewFormProps {
  onOptionSelected: (keys: string[]) => void;
  onCancel: () => void;
  onGenerate: (data: GenerateReviewFormData) => void;
  isGenerating: boolean;
  initialSelectedOptions: Set<string>;
}

export const GenerateReviewForm: React.FC<GenerateReviewFormProps> = ({
  onOptionSelected,
  onCancel,
  onGenerate,
  isGenerating,
  initialSelectedOptions,
}) => {
  const { data: definitions, isLoading: isLoadingDefinitions } = useDefinitions();

  const processedOptions = React.useMemo(() => {
    if (!definitions) return [];
    return processDefinitions(definitions)
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [definitions]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<GenerateReviewFormData>({
    resolver: zodResolver(generateReviewSchema),
    mode: "onBlur",
    defaultValues: {
      selectedOptions: Array.from(initialSelectedOptions),
      representingInput: "",
      considerationInput: "",
    },
  });

  React.useEffect(() => {
    setValue("selectedOptions", Array.from(initialSelectedOptions));
  }, [initialSelectedOptions]);

  const selectedOptions = watch("selectedOptions");
  const selectedOptionsSet = new Set(selectedOptions);

  const handleOptionChange = (key: string, checked: boolean) => {
    const newSelected = new Set(selectedOptions);

    if (checked) {
      if (key === "custom-review") {
        newSelected.clear();
        newSelected.add(key);
      } else {
        newSelected.delete("custom-review");
        newSelected.add(key);
      }
    } else {
      newSelected.delete(key);
    }

    const newSelectedArray = Array.from(newSelected);
    setValue("selectedOptions", newSelectedArray);
    onOptionSelected(newSelectedArray);
  };

  const handleSelectAll = () => {
    const nonCustomOptions = processedOptions.filter((option) => option.key !== "custom-review");
    let newSelected: Set<string>;

    const allNonCustomSelected = nonCustomOptions.every((option) =>
      selectedOptionsSet.has(option.key)
    );

    if (allNonCustomSelected && nonCustomOptions.length > 0) {
      newSelected = new Set();
    } else {
      newSelected = new Set(nonCustomOptions.map((option) => option.key));
    }
    const newSelectedArray = Array.from(newSelected);
    setValue("selectedOptions", newSelectedArray);
    onOptionSelected(newSelectedArray);
  };

  const onSubmit = (data: GenerateReviewFormData) => {
    onGenerate(data);
    onCancel();
  };

  const handleCancel = () => {
    onCancel();
  };

  if (isLoadingDefinitions) {
    return (
      <div className="flex flex-col gap-3">
        <Loader className="animate-spin" />
        <p>Loading review options...</p>
      </div>
    );
  }

  const hasSelectedOptions = selectedOptions.length > 0;

  return (
    <form onSubmit={handleSubmit(onSubmit, (errors) => {
      console.log(errors);
    })} className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm">Review Options</h3>
        <Button
          type="button"
          variant={"link"}
          className="cursor-pointer text-sm font-medium size-auto p-0 text-primary-300"
          onClick={handleSelectAll}
        >
          {(() => {
            const nonCustomOptions = processedOptions.filter(
              (option) => option.key !== "custom-review"
            );
            const allNonCustomSelected =
              nonCustomOptions.every((option) => selectedOptionsSet.has(option.key)) &&
              nonCustomOptions.length > 0;
            return allNonCustomSelected ? "Deselect All" : "Select All";
          })()}
        </Button>
      </div>
      <div className="flex flex-col p-4 gap-2 bg-muted rounded-md border max-h-40 overflow-y-auto">
        {processedOptions.map((option) => (
          <div key={option.key} className="flex items-center gap-2">
            <Checkbox
              className="size-4 border bg-background data-[state=checked]:bg-primary-300 text-neutral-white"
              id={option.key}
              checked={selectedOptionsSet.has(option.key)}
              onCheckedChange={(checked) => handleOptionChange(option.key, checked === true)}
              disabled={
                isGenerating ||
                (option.key === "custom-review" &&
                  selectedOptionsSet.size > 0 &&
                  !selectedOptionsSet.has("custom-review"))
              }
            />
            <FieldLabel htmlFor={option.key} className="text-sm text-neutral-300 font-normal">
              {option.text}
            </FieldLabel>
          </div>
        ))}
      </div>
      {errors.selectedOptions && (
        <p className="text-danger-200 text-xs">{errors.selectedOptions.message}</p>
      )}

      <FieldGroup className="flex flex-col gap-1.5">
        <Controller
          name="representingInput"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} className="grid gap-1.5">
              <FieldLabel className="text-xs font-medium">Who are you representing?</FieldLabel>
              <FieldContent>
                <Input
                  {...field}
                  placeholder="Enter client or organisation name"
                  disabled={isGenerating}
                  className="h-8 text-xs"
                />
              </FieldContent>
              {fieldState.invalid && (
                <FieldError className="text-danger-200 text-xs" errors={[fieldState.error]} />
              )}
            </Field>
          )}
        />

        <Controller
          name="considerationInput"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} className="grid gap-1.5">
              <FieldLabel className="text-xs font-medium">Something to consider?</FieldLabel>
              <FieldContent>
                <Textarea
                  className="outline-none resize-none bg-transparent px-4 py-2 min-h-[60px] max-h-[120px] text-xs "
                  {...field}
                  placeholder="Enter any specific considerations or requirements"
                  disabled={isGenerating}
                />
              </FieldContent>
              {fieldState.invalid && (
                <FieldError className="text-danger-200 text-xs" errors={[fieldState.error]} />
              )}
            </Field>
          )}
        />
      </FieldGroup>

      <div className="flex justify-end gap-2 mt-2">
        <Button
          type="button"
          variant={"ghost"}
          size="sm"
          className="border font-medium text-xs"
          onClick={handleCancel}
          disabled={isGenerating}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          size="sm"
          disabled={!hasSelectedOptions || isGenerating}
          className="disabled:bg-primary-200"
        >
          {isGenerating ? (
            <>
              <Loader className="animate-spin" />
              <span className="ml-2">Analysing...</span>
            </>
          ) : (
            "Analyse"
          )}
        </Button>
      </div>
    </form>
  );
};
